import { GroupId } from '@miles/domain/EntityIds'
import { CheckoutReduction } from '@miles/domain/Store/Models/Reduction/CheckoutReduction'
import { ReductionRead } from '@miles/domain/Store/Models/Reduction/ReductionRead'
import { TFunction } from 'i18next'
import { match } from 'ts-pattern'
import { ReadonlyDeep } from 'type-fest'
import { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep'
import { LightGroup } from 'domain/models/Group/EntityGroup'
import { FeesType } from 'domain/models/ValueObject/FeesType'

export const reductionFormat = (
  adjustmentAmount: number,
  reductionAmountType: FeesType,
  reductionAmount: number,
  formatter: (amount: number) => string
): string => {
  if (reductionAmountType === 'PERCENT') {
    return `(${reductionAmount} %) - ${formatter(adjustmentAmount)}`
  }
  return formatter(-adjustmentAmount)
}

export const discountFormat = (
  amount: number,
  value: number,
  amountType: FeesType,
  formatter: (amount: number) => string
): string =>
  match(amountType)
    .with('PERCENT', () => `(${value} %) - ${formatter(amount)}`)
    .with('FIXED', () => `- ${formatter(amount)}`)
    .exhaustive()

export const calculateTotalReduction = (
  price: number,
  reductions: ReadonlyObjectDeep<(CheckoutReduction | ReductionRead)[]>
): number =>
  Math.round(
    reductions.reduce(
      (acc, reduction) =>
        match(reduction)
          .with(
            { amountType: 'PERCENT' },
            (reduction) => acc + (price * reduction.amount) / 100
          )
          .with({ amountType: 'FIXED' }, (reduction) => acc + reduction.amount)
          .exhaustive(),
      0
    )
  )

export const reductionTitle = (
  reduction: ReadonlyDeep<CheckoutReduction>,
  t: TFunction<'checkout', undefined>,
  group?: ReadonlyDeep<{
    id: GroupId
    name: string
  } | null>
): string =>
  match(reduction)
    .with(
      { reductionType: 'GENDER' },
      ({ gender }) => `${t('cart.reductions')} ${t(`enum.gender.${gender}`)}`
    )
    .with(
      { reductionType: 'GROUP' },
      ({ groupId }) =>
        `${t('cart.reductions')} ${groupId === group?.id ? group.name : ''}`
    )
    .with({ reductionType: 'ALL' }, () => t('cart.reductions'))
    .otherwise(
      (reduction) =>
        `${t('cart.reductions')} ${t(
          `enum.reductionType.${reduction.reductionType}`
        )}`
    )

export const translateAdjustmentFromDiscount = (
  reduction: CheckoutReduction,
  group: LightGroup | null,
  t: TFunction<'checkout', undefined>
): string => {
  return match(reduction)
    .with(
      { reductionType: 'GENDER' },
      ({ gender }) => `${t('cart.reductions')} ${t(`enum.gender.${gender}`)}`
    )
    .with(
      { reductionType: 'GROUP' },
      ({ groupId }) =>
        `${t('cart.reductions')} ${groupId === group?.id ? group.name : ''}`
    )
    .with({ reductionType: 'ALL' }, () => t('cart.reductions'))
    .otherwise(
      (reduction) =>
        `${t('cart.reductions')} ${t(
          `enum.reductionType.${reduction.reductionType}`
        )}`
    )
}
